import React, { useState, useEffect, useRef } from 'react';
import './TaskFilters.css';
import { useAuth } from '../../../../features/auth/contexts/AuthContext';
import { useFilterContext } from '../../contexts/FilterContext';
import UserMenu from '../../../../features/auth/components/UserMenu/UserMenu';
import Menu from '../../../../ui/components/Menu/Menu';
import MenuItem from '../../../../ui/components/Menu/MenuItem';
import SignupLoginModal from '../../../../features/auth/components/SignupLoginModal/SignupLoginModal';
import AboutModal from '../../../../ui/components/AboutModal/AboutModal';
import InviteCodeModal from '../../../../features/auth/components/InviteCodeModal/InviteCodeModal';
import { SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS } from '../../../../shared/constants/featureFlags';
import { useHelpShowcase } from '../../../../shared/contexts/HelpShowcaseContext';
import { trackEvent } from '../../../../shared/utils/analytics';
import { PRIORITIZE_SECTIONS } from '../../constants/prioritizeSections';

function TaskFilters({
  visiblePrioritizeSections,
  onTogglePrioritizeSectionVisibility,
  leftContent = null,
  toolbarRow = null,
  modeSwitchInToolbar = false
} = {}) {
  const { filter, setFilter, timeFilter, setTimeFilter } = useFilterContext();
  const openHelpShowcase = useHelpShowcase();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTaskViewMenuOpen, setIsTaskViewMenuOpen] = useState(false);
  const [isPrioritizeMenuOpen, setIsPrioritizeMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('login');
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isInviteCodeModalOpen, setIsInviteCodeModalOpen] = useState(false);
  const { userEmail, logout, isLoggedIn } = useAuth();
  const userPillRef = useRef(null);
  const taskViewMenuRef = useRef(null);
  const prioritizeMenuRef = useRef(null);
  /* Desktop: mode switch lives only in the task table toolbar row; never show duplicate Organize/Prioritize here. */
  const hideModeButtons = modeSwitchInToolbar;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userPillRef.current && !userPillRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (taskViewMenuRef.current && !taskViewMenuRef.current.contains(event.target)) {
        setIsTaskViewMenuOpen(false);
      }
      if (prioritizeMenuRef.current && !prioritizeMenuRef.current.contains(event.target)) {
        setIsPrioritizeMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isTaskViewMenuOpen || isPrioritizeMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen, isTaskViewMenuOpen, isPrioritizeMenuOpen]);

  const hasHeaderContent = !!(leftContent || toolbarRow);

  return (
    <div className={`task-filters ${isScrolled ? 'scrolled' : ''}${hasHeaderContent ? ' task-filters-with-header-content' : ''}`}>
      <div className="task-filters-main">
        <div className="task-filters-left">
          {leftContent && <div className="task-filters-left-content">{leftContent}</div>}
          {!hideModeButtons && (
          <>
          <div className="task-filter-menu-container" ref={taskViewMenuRef}>
            <div className={`task-filter-button text-non-selectable task-filter-button-task-view ${filter === 'all' || filter === 'incomplete' ? 'active' : ''}`}>
              <div 
                className="task-view-text-part"
                onClick={() => {
                  trackEvent('organize_clicked');
                  if (filter !== 'all' && filter !== 'incomplete') {
                    setFilter('all');
                  }
                }}
                style={{ cursor: filter !== 'all' && filter !== 'incomplete' ? 'pointer' : (SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS ? 'pointer' : 'default') }}
              >
                <svg width="12" height="12" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                  <rect width="6" height="6" x="10" y="26" fill="currentColor" rx="1" ry="1"/>
                  <path fill="currentColor" d="M15 16h-4a1 1 0 0 0-1 1v1.2H5.8V12H7a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h1.2v17.8H11a.8.8 0 1 0 0-1.6H5.8v-8.4H10V21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1"/>
                  <path fill="currentColor" d="M33 8H10v2h23a1 1 0 0 0 0-2"/>
                  <path fill="currentColor" d="M33 18H18v2h15a1 1 0 0 0 0-2"/>
                  <path fill="currentColor" d="M33 28H18v2h15a1 1 0 0 0 0-2"/>
                </svg>
                Organize
              </div>
              {SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && (
                <>
                  <div className="task-view-separator"></div>
                  <button
                    className="task-view-dropdown-button text-non-selectable"
                    onClick={() => {
                      if (filter !== 'all' && filter !== 'incomplete') {
                        setFilter('all');
                      } else {
                        setIsTaskViewMenuOpen(!isTaskViewMenuOpen);
                      }
                    }}
                    aria-label="Organize menu"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
            {SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && (filter === 'all' || filter === 'incomplete') && (
              <Menu isOpen={isTaskViewMenuOpen} onClose={() => setIsTaskViewMenuOpen(false)} align="left">
                <MenuItem
                  isActive={filter === 'incomplete'}
                  onClick={() => {
                    setFilter(filter === 'incomplete' ? 'all' : 'incomplete');
                    setIsTaskViewMenuOpen(false);
                  }}
                >
                  Incomplete
                </MenuItem>
                <MenuItem
                  isActive={timeFilter === 'overdue'}
                  onClick={() => {
                    setTimeFilter(timeFilter === 'overdue' ? null : 'overdue');
                    setIsTaskViewMenuOpen(false);
                  }}
                >
                  Overdue
                </MenuItem>
                <MenuItem
                  isActive={timeFilter === 'today'}
                  onClick={() => {
                    setTimeFilter(timeFilter === 'today' ? null : 'today');
                    setIsTaskViewMenuOpen(false);
                  }}
                >
                  Today
                </MenuItem>
                <MenuItem
                  isActive={timeFilter === 'next7days'}
                  onClick={() => {
                    setTimeFilter(timeFilter === 'next7days' ? null : 'next7days');
                    setIsTaskViewMenuOpen(false);
                  }}
                >
                  Next 7 days
                </MenuItem>
              </Menu>
            )}
          </div>
          {SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && visiblePrioritizeSections != null && onTogglePrioritizeSectionVisibility ? (
            <div className="task-filter-menu-container task-filter-menu-container-prioritize" ref={prioritizeMenuRef}>
              <div className={`task-filter-button text-non-selectable task-filter-button-task-view task-filter-button-prioritize-menu ${filter === 'prioritize' ? 'active' : ''}`}>
                <div
                  className="task-view-text-part"
                  onClick={() => {
                    if (filter !== 'prioritize') {
                      trackEvent('prioritize_clicked');
                      setFilter('prioritize');
                    }
                  }}
                  style={{ cursor: filter === 'prioritize' ? 'default' : 'pointer' }}
                >
                  <svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                    <path fill="currentColor" d="M216 48H40a8 8 0 0 0-8 8v152a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16v-48h48v16a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16V56a8 8 0 0 0-8-8M88 208H48v-80h40Zm0-96H48V64h40Zm64 32h-48V64h48Zm56 32h-40v-48h40Zm0-64h-40V64h40Z"/>
                  </svg>
                  Prioritize
                </div>
                <div className="task-view-separator"></div>
                <button
                  className="task-view-dropdown-button text-non-selectable"
                  onClick={() => {
                    if (filter !== 'prioritize') {
                      trackEvent('prioritize_clicked');
                      setFilter('prioritize');
                    }
                    setIsPrioritizeMenuOpen(prev => !prev);
                  }}
                  aria-label="Prioritize sections menu"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
              </div>
              <Menu isOpen={isPrioritizeMenuOpen} onClose={() => setIsPrioritizeMenuOpen(false)} align="left">
                {PRIORITIZE_SECTIONS.map(({ key, label }) => (
                  <MenuItem
                    key={key}
                    isActive={visiblePrioritizeSections.has(key)}
                    onClick={() => {
                      onTogglePrioritizeSectionVisibility(key);
                      setIsPrioritizeMenuOpen(false);
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          ) : (
            <button
              className={`task-filter-button text-non-selectable task-filter-button-prioritize ${filter === 'prioritize' ? 'active' : ''}`}
              onClick={() => {
                if (filter !== 'prioritize') {
                  trackEvent('prioritize_clicked');
                }
                setFilter('prioritize');
              }}
            >
              <svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                <path fill="currentColor" d="M216 48H40a8 8 0 0 0-8 8v152a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16v-48h48v16a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16V56a8 8 0 0 0-8-8M88 208H48v-80h40Zm0-96H48V64h40Zm64 32h-48V64h48Zm56 32h-40v-48h40Zm0-64h-40V64h40Z"/>
              </svg>
              Prioritize
            </button>
          )}
          </>
          )}
        </div>
        <div className="task-filters-right">
          <div className="task-filters-right-desktop">
            {isLoggedIn ? (
              <div className="user-pill-container" ref={userPillRef}>
                <button
                  type="button"
                  className="invite-pill text-non-selectable"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    setIsInviteCodeModalOpen(true);
                  }}
                  aria-label="Invite others"
                >
                  <span className="invite-pill-text">Invite others</span>
                </button>
                <div
                  className="user-pill text-non-selectable"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  style={{ cursor: 'pointer' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="6" r="4"/>
                      <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/>
                    </g>
                  </svg>
                  {userEmail || 'User'}
                </div>
                {isUserMenuOpen && (
                  <UserMenu
                    userEmail={userEmail}
                    onLogout={logout}
                    onHelpClick={openHelpShowcase ? () => { openHelpShowcase(); setIsUserMenuOpen(false); } : undefined}
                    onAboutClick={() => { setIsAboutModalOpen(true); setIsUserMenuOpen(false); }}
                  />
                )}
              </div>
            ) : (
              <>
                <button
                  className="task-filter-button text-non-selectable task-filter-button-login"
                  onClick={() => {
                    setModalMode('login');
                    setIsModalOpen(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="task-filter-button-icon">
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="6" r="4"/>
                      <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/>
                    </g>
                  </svg>
                  Log in
                </button>
                <button
                  className="task-filter-button text-non-selectable task-filter-button-signup"
                  onClick={() => {
                    const saveButton = document.querySelector('[data-save-button]');
                    if (saveButton && !saveButton.disabled) {
                      saveButton.click();
                    } else {
                      setModalMode('signup');
                      setIsModalOpen(true);
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="task-filter-button-icon">
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="6" r="4"/>
                      <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/>
                    </g>
                  </svg>
                  Sign up
                </button>
              </>
            )}
          </div>
          {hasHeaderContent && (
            <div className="task-filters-header-mobile-logo" aria-hidden="true">
              <svg width="130" height="40" viewBox="0 0 130 40" xmlns="http://www.w3.org/2000/svg">
                <text x="10" y="28" fontFamily="system-ui, -apple-system, sans-serif" fontSize="24" fontWeight="600">
                  <tspan fill="#444" fontSize="24">pico</tspan><tspan fill="#333" fontSize="28" dx="1">todo</tspan>
                </text>
              </svg>
              <span className="task-view-logo-badge">BETA</span>
            </div>
          )}
        </div>
      </div>
      {toolbarRow && <div className="task-filters-toolbar-row">{toolbarRow}</div>}
      <SignupLoginModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tasks={[]}
        initialMode={modalMode}
        loginOnly={false}
        source="task_filters"
      />
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
      <InviteCodeModal isOpen={isInviteCodeModalOpen} onClose={() => setIsInviteCodeModalOpen(false)} />
    </div>
  );
}

export default TaskFilters;

